// client/src/api/services/dashboardService.ts
import { apiClient } from '../client';
import { ENDPOINTS } from '../endpoints';
import { getErrorMessage } from '@/utils/errorUtils';
import { AxiosError } from "axios";

export type WalletCryptoSymbol = 'USDT' | 'USDC';
export type WalletCryptoChain = 'BSC_TESTNET' | 'SOLANA_DEVNET';

export interface WalletCryptoTopupInitPayload {
  amount: number;
  crypto: WalletCryptoSymbol;
  chain: WalletCryptoChain;
  baseFiat: string;
}

export interface WalletCryptoTopupInitResponse {
  transactionId?: string;
  reference?: string;
  address?: string;
  network?: string;
  chain?: string;
  crypto?: string;
  baseFiat?: string;
  amountInCrypto?: number;
  platformFeeInCrypto?: number;
  amountInFiat?: number;
  validityPeriod?: number;
  expiresAt?: string;
  amount?: number;
  currency?: string;
}

export interface WalletCryptoTopupVerifyResponse {
  success?: boolean;
  message?: string;
  data?: {
    success?: boolean;
    message?: string;
  };
}

// KX-39 (T-32) — Q3: fiat balance split for the operator wallet. This is the
// LOCAL-CURRENCY earnings balance and is entirely separate from the KXT token
// balance (kxtService). `available` is withdrawable now; `pending` is accrued
// fiat earnings locked until KYC is verified. Minor-unit strings are the exact
// source of truth — never do money arithmetic on them in the browser.
export interface WalletFiatBalance {
  available: string;        // decimal string, e.g. "50.00"
  availableMinor: string;   // integer minor units as a string
  pending: string;          // decimal string, e.g. "12.50"
  pendingMinor: string;     // integer minor units as a string
  currency: string;         // ISO-4217, from the record — never hardcoded
  kycRequiredToWithdraw: boolean;
}

interface WalletBalanceEnvelope {
  success?: boolean;
  data?: WalletFiatBalance;
}

// export interface DashboardData {
//   onlineHotspots: number;
//   onlineChange: number;
//   averageUptime: number;
//   totalEarnings: number;
//   offlineHotspots: number;
// }

/**
 * Fetch dashboard cards data
 */
export const getBalance = async (): Promise<unknown> => {
  try {
    const response = await apiClient.get<unknown>(ENDPOINTS.WALLET.BALANCE);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching dashboard cards:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch dashboard cards');
  }
};

/**
 * KX-39 (T-32) — typed fiat balance split (available / pending) for the operator
 * wallet. Separate from the KXT token balance. Tolerates both `{ data: {...} }`
 * and a flat payload.
 */
export const getFiatBalance = async (): Promise<WalletFiatBalance> => {
  const response = await apiClient.get<WalletBalanceEnvelope & Partial<WalletFiatBalance>>(
    ENDPOINTS.WALLET.BALANCE
  );
  const body = response.data;
  return (body.data ?? (body as WalletFiatBalance));
};
export const getSubscription = async (): Promise<unknown> => {
  try {
    const response = await apiClient.get<unknown>(ENDPOINTS.WALLET.BALANCE);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching dashboard cards:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch dashboard cards');
  }
};
export const getTransaction = async (
  type?: 'top-up' | 'subscriptions',
  page = 1,
  limit = 10
): Promise<unknown> => {
  try {
    const response = await apiClient.get<unknown>(ENDPOINTS.WALLET.TRANSACTIONS, {
      params: {
        ...(type ? { type } : {}),
        page,
        limit,
      },
    });
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching dashboard cards:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch dashboard cards');
  }
};
export const AddFund = async (data): Promise<unknown> => {
  try {
    const response = await apiClient.post<unknown>(ENDPOINTS.WALLET.ADDFUND, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching dashboard cards:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch dashboard cards');
  }
};
export const AddWithdraw = async (data): Promise<unknown> => {
  try {
    const response = await apiClient.post<unknown>(ENDPOINTS.WALLET.ADDWITHDRAW, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching dashboard cards:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch dashboard cards');
  }
};

export const initializeCryptoTopup = async (
  data: WalletCryptoTopupInitPayload
): Promise<unknown> => {
  try {
    const response = await apiClient.post<unknown>(ENDPOINTS.WALLET.CELO_TOPUP_CRYPTO_INIT, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error initializing crypto topup:', error);
    throw new Error(getErrorMessage(error) || 'Failed to initialize crypto topup');
  }
};

export const verifyCryptoTopup = async (
  reference: string
): Promise<WalletCryptoTopupVerifyResponse> => {
  try {
    const response = await apiClient.get<WalletCryptoTopupVerifyResponse>(
      `${ENDPOINTS.WALLET.CELO_TOPUP_CRYPTO_VERIFY}?reference=${encodeURIComponent(reference)}`
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error verifying crypto topup:', error);
    const errorMessage =
      getErrorMessage(error) ||
      'Failed to verify crypto topup';
    throw new Error(errorMessage);
  }
};

export const buyKxtWithBalance = async (
  data: { amountUsd: number; walletSource: 'flat' | 'celo' }
): Promise<unknown> => {
  try {
    const response = await apiClient.post<unknown>(ENDPOINTS.WALLET.BUY_KXT_BALANCE, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error buying KXT with balance:', error);
    throw new Error(getErrorMessage(error) || 'Failed to buy KXT with balance');
  }
};

export const buyKxtWithCrypto = async (
  data: { amountUsd: number }
): Promise<unknown> => {
  try {
    const response = await apiClient.post<unknown>(ENDPOINTS.WALLET.BUY_KXT_CRYPTO, data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error buying KXT with crypto:', error);
    throw new Error(getErrorMessage(error) || 'Failed to buy KXT with crypto');
  }
};

export default {
  getBalance,
  getFiatBalance,
  getTransaction,
  getSubscription,
  AddFund,
  AddWithdraw,
  initializeCryptoTopup,
  verifyCryptoTopup,
  buyKxtWithBalance,
  buyKxtWithCrypto
};
